import { FilterPills, nonEmptyPills } from '@/components/FilterPills';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSetSearchParams } from '@/lib/searchParams';
import {
    CATALOG_RECALL_STATUSES,
    type CatalogRecallStatus,
    RECALLS_DEFAULT_FILTER,
    type RecallCounts,
    type RecallsFilter,
} from '@/state';
import { type FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RecallsTable } from './RecallsTable';
import { CATALOG_STATUS_LABELS, catalogMatchesCount, catalogRecallCount } from './recallStatus';
import { useRecallJobRequest } from './useRecallJobRequest';

/** The catalog (drafts and approved), under URL-driven filters: status, has matches, re-review, new suggestions. */
export const RecallsCatalogTab: FC<{ counts: RecallCounts | undefined }> = ({ counts }) => {
    const [searchParams] = useSearchParams();
    const filter = useMemo(() => filterFromParams(searchParams), [searchParams]);
    const { request, isRequesting, canRequest } = useRecallJobRequest();

    const handleSweep = () =>
        request(
            { kind: 'sweep', notify: true },
            'Apply approved recalls',
            (jobId) => `Job ${jobId} re-applies every approved recall's rules; newly matched owners are notified.`,
        );

    return (
        <>
            <CatalogFilters filter={filter} statusValue={catalogStatus(searchParams)} counts={counts} />
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                    <div className="flex flex-col gap-1.5">
                        <CardTitle>Recalls</CardTitle>
                        <CardDescription>Newest publish first. Tap a row to review the recall.</CardDescription>
                    </div>
                    <Button
                        variant="outline"
                        onClick={handleSweep}
                        disabled={isRequesting || !canRequest}
                        title="Re-apply every approved recall against the catalog as it stands, as the daily sweep does (matches and verdicts rebuilt; new matches notify), then recount the topic stats"
                    >
                        {isRequesting ? 'Requesting…' : 'Apply approved recalls'}
                    </Button>
                </CardHeader>
                <CardContent>
                    <RecallsTable filter={filter} emptyText="No recalls match these filters." />
                </CardContent>
            </Card>
        </>
    );
};

const CatalogFilters: FC<{
    filter: RecallsFilter;
    statusValue: CatalogRecallStatus | undefined;
    counts: RecallCounts | undefined;
}> = ({ filter, statusValue, counts }) => {
    const setParams = useSetSearchParams();
    const statusPills = nonEmptyPills(CATALOG_RECALL_STATUSES, CATALOG_STATUS_LABELS, counts?.byStatus, statusValue);

    return (
        <div className="flex flex-col gap-2 rounded border bg-background p-3">
            <FilterPills
                label="Status"
                items={statusPills}
                selected={statusValue}
                allCount={catalogRecallCount(counts)}
                onSelect={(key) =>
                    setParams({ status: key, matches: undefined, rereview: undefined, suggestions: undefined })
                }
            />
            <FilterPills
                label="Review"
                items={[
                    { key: 'matches', label: 'Has matched units', count: catalogMatchesCount(counts, statusValue) },
                    { key: 'rereview', label: 'Re-review pending', count: counts?.reReview },
                    { key: 'suggestions', label: 'New suggestions', count: counts?.suggestions },
                ]}
                selected={
                    filter.hasMatches
                        ? 'matches'
                        : filter.reReview
                          ? 'rereview'
                          : filter.suggestions
                            ? 'suggestions'
                            : undefined
                }
                onSelect={(key) =>
                    setParams({
                        status: statusValue,
                        matches: key === 'matches' ? '1' : undefined,
                        rereview: key === 'rereview' ? '1' : undefined,
                        suggestions: key === 'suggestions' ? '1' : undefined,
                    })
                }
            />
        </div>
    );
};

function filterFromParams(params: URLSearchParams): RecallsFilter {
    return {
        ...RECALLS_DEFAULT_FILTER,
        status: catalogStatus(params) ?? 'catalog',
        hasMatches: params.get('matches') === '1',
        reReview: params.get('rereview') === '1',
        suggestions: params.get('suggestions') === '1',
    };
}

/** A status outside the catalog in an old link reads as the whole catalog. */
function catalogStatus(params: URLSearchParams): CatalogRecallStatus | undefined {
    const status = params.get('status');
    return CATALOG_RECALL_STATUSES.find((s) => s === status);
}
